import { useState } from "react";
import { Link } from "react-router-dom";

interface PreviewData {
  title: string;
  excerpt: string;
  category: string;
  category_color: string;
  author: string;
  date: string;
  read_time: string;
  image: string;
  featured: boolean;
  slug: string;
  content: string;
  secondary_categories?: string[];
}

interface ArticlePreviewProps {
  data: PreviewData;
}

export default function ArticlePreview({ data }: ArticlePreviewProps) {
  const [activeView, setActiveView] = useState<"card" | "article" | "featured">("card");

  const categoryColor = data.category_color || "bg-blue-500";
  const imageUrl = data.image || "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-preview&orientation=landscape";
  const articleUrl = `/articulo/${data.slug || "preview"}`;

  return (
    <div className="space-y-4">
      {/* View toggle */}
      <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1 w-fit">
        {[
          { id: "card" as const, label: "Tarjeta", icon: "ri-layout-grid-line" },
          { id: "featured" as const, label: "Destacada", icon: "ri-vip-crown-line" },
          { id: "article" as const, label: "Artículo", icon: "ri-article-line" },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeView === v.id
                ? "bg-white text-[#0B1F3A] shadow-sm"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <i className={v.icon} />
            {v.label}
          </button>
        ))}
      </div>

      {/* Card view */}
      {activeView === "card" && (
        <div className="max-w-sm mx-auto">
          <article className="bg-white rounded-2xl overflow-hidden border border-slate-100">
            <div className="block h-64 overflow-hidden bg-[#0B1F3A]">
              <img
                src={imageUrl}
                alt={data.title}
                className="w-full h-full object-cover object-center"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-preview&orientation=landscape";
                }}
              />
            </div>
            <div className="p-5">
              <span
                className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-3 ${categoryColor}`}
              >
                {data.category || "Categoría"}
              </span>
              {data.secondary_categories && data.secondary_categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {data.secondary_categories.map((sc) => (
                    <span
                      key={sc}
                      className="inline-block text-slate-500 text-[9px] font-medium px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200"
                    >
                      {sc}
                    </span>
                  ))}
                </div>
              )}
              <h3
                className="text-[#0B1F3A] font-bold leading-snug mb-2"
                style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.05rem" }}
              >
                {data.title || "Título de la noticia"}
              </h3>
              <p
                className="text-slate-400 text-sm line-clamp-2 mb-4 leading-relaxed"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {data.excerpt || "Extracto de la noticia..."}
              </p>
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center gap-2 text-xs text-slate-400"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <span>{data.date || "1 May 2026"}</span>
                  <span>·</span>
                  <span>{data.read_time || "4 min"}</span>
                </div>
                <Link
                  to={articleUrl}
                  target="_blank"
                  className="text-xs text-slate-600 font-semibold flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer"
                >
                  Leer <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>
          </article>
        </div>
      )}

      {/* Featured / Hero view */}
      {activeView === "featured" && (
        <div className="max-w-lg mx-auto">
          <Link to={articleUrl} target="_blank" className="block">
            <article className="relative rounded-2xl overflow-hidden h-[420px] bg-[#0B1F3A] cursor-pointer group">
              <div className="absolute inset-0">
                <img
                  src={imageUrl}
                  alt={data.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-preview&orientation=landscape";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071529] via-[#071529]/60 to-[#071529]/20" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <span
                  className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3 ${categoryColor}`}
                >
                  {data.category || "Categoría"}
                </span>
                <h2
                  className="text-white font-bold text-xl leading-snug mb-3"
                  style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.35rem" }}
                >
                  {data.title || "Título destacado"}
                </h2>
                <p
                  className="text-slate-300 text-sm line-clamp-2 mb-4"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {data.excerpt || "Extracto de la noticia destacada..."}
                </p>
                <div
                  className="flex items-center gap-3 text-xs text-slate-400"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <span>{data.date || "1 May 2026"}</span>
                  <span>·</span>
                  <span>{data.read_time || "4 min"}</span>
                </div>
              </div>
            </article>
          </Link>
        </div>
      )}

      {/* Article page view */}
      {activeView === "article" && (
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="h-80 overflow-hidden bg-[#0B1F3A]">
            <img
              src={imageUrl}
              alt={data.title}
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-preview&orientation=landscape";
              }}
            />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span
                className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${categoryColor}`}
              >
                {data.category || "Categoría"}
              </span>
              {data.secondary_categories && data.secondary_categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {data.secondary_categories.map((sc) => (
                    <span
                      key={sc}
                      className="inline-block text-slate-500 text-[9px] font-medium px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200"
                    >
                      {sc}
                    </span>
                  ))}
                </div>
              )}
              <span className="text-slate-400 text-xs">{data.date || "1 May 2026"}</span>
              <span className="text-slate-300 text-xs">·</span>
              <span className="text-slate-400 text-xs">{data.read_time || "4 min"}</span>
            </div>
            <h1
              className="text-[#0B1F3A] font-bold text-2xl leading-snug mb-4"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {data.title || "Título del artículo"}
            </h1>
            <p
              className="text-slate-500 text-sm leading-relaxed mb-6 whitespace-pre-line"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {data.excerpt || "Extracto del artículo..."}
            </p>
            <div className="flex items-center gap-3 py-4 border-t border-slate-100">
              <div className="w-10 h-10 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white text-sm font-bold">
                CB
              </div>
              <div>
                <p className="text-[#0B1F3A] font-semibold text-sm">{data.author || "Carmen Bretón"}</p>
                <p className="text-slate-400 text-xs">Editora Principal</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-5">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2">
                Contenido completo
              </p>
              {data.content?.trim().startsWith("<") ? (
                <div
                  className="article-body text-slate-500 text-sm leading-relaxed max-w-none"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: data.content }}
                />
              ) : (
                <p className="text-slate-500 text-sm leading-relaxed whitespace-pre-line">
                  {data.content || "Aquí iría el contenido completo del artículo..."}
                </p>
              )}
            </div>
            <div className="mt-6 text-center">
              <Link
                to={articleUrl}
                target="_blank"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line" />
                Ver artículo completo
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}